import axios from 'axios'

const authError = () => Object.assign(new Error('Open Flype from Shopify Admin and try again.'), { code: 'SHOPIFY_BRIDGE_UNAVAILABLE' })
let activeShop
let pendingRecovery

export const getShopDomain = () => {
    if (typeof window === 'undefined') return null
    const shop = new URLSearchParams(window.location.search).get('shop') || activeShop
    return typeof shop === 'string' && /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(shop) ? shop : null
}

export const getSessionToken = async (timeoutMs = 15000) => {
    if (typeof window === 'undefined' || typeof window.shopify?.idToken !== 'function') throw authError()
    let timeout
    try {
        const token = await Promise.race([
            window.shopify.idToken(),
            new Promise((_, reject) => { timeout = setTimeout(() => reject(authError()), timeoutMs) }),
        ])
        if (typeof token !== 'string' || !token) throw authError()
        return token
    } finally {
        clearTimeout(timeout)
    }
}

// The private client never sends Shopify credentials to Firebase or third parties.
const api = axios.create({ timeout: 20000 })
api.interceptors.request.use(async config => {
    if (typeof config.url !== 'string' || !/^\/(api\/|activation(?:$|\?))/.test(config.url) || config.baseURL) {
        throw new Error('Shopify authentication can only be sent to local app API routes')
    }
    const url = new URL(config.url, window.location.origin)
    if (url.origin !== window.location.origin || !/^\/(api\/|activation$)/.test(url.pathname)) throw new Error('Invalid API origin')
    config.headers = { ...config.headers, Authorization: `Bearer ${await getSessionToken()}` }
    const shop = getShopDomain()
    if (shop) config.headers['X-Shopify-Shop-Domain'] = shop
    return config
})
api.interceptors.response.use(response => response, async error => {
    const response = error.response
    // This 401 occurs before the handler runs, so one retry cannot replay a
    // billing/activation effect. Never retry timeouts or server failures here.
    if (response?.status === 401 && response.data?.error === 'SHOPIFY_SESSION_INVALID' &&
        error.config && !error.config.shopifySessionRetried) {
        return api({ ...error.config, shopifySessionRetried: true })
    }
    // This error means Shopify rejected authentication, before accepting the
    // operation. Reacquire offline credentials using the verified live session.
    if (response?.status === 401 && response.data?.error === 'SHOPIFY_REAUTH_REQUIRED' &&
        error.config && error.config.url !== '/api/session' && !error.config.shopifyAccessRetried) {
        if (!pendingRecovery) pendingRecovery = api.post('/api/session').finally(() => { pendingRecovery = null })
        await pendingRecovery
        return api({ ...error.config, shopifyAccessRetried: true })
    }
    throw error
})

export const startSession = async () => {
    const { data } = await api.post('/api/session')
    if (!data?.customToken || !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(data.shop)) throw authError()
    activeShop = data.shop
    return data
}

export const removeLegacyCredentials = () => {
    const url = new URL(window.location.href)
    for (const key of ['uid', 'token', 'auth', 'expires_at', 'hmac', 'signature']) url.searchParams.delete(key)
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
}

export const navigateFromApp = (path, { admin = true, newContext = false } = {}) => {
    let url
    if (admin && path.startsWith('/')) {
        url = `shopify://admin${path}`
    } else {
        url = new URL(path, window.location.origin).href
        if (!/^https?:\/\//.test(url)) throw new Error('Invalid navigation URL')
    }
    // Current App Bridge intercepts standard browser navigation APIs.
    window.open(url, newContext ? '_blank' : admin ? '_top' : '_self')
}

export default api
